import { SNOOZED_FEED_GROUP } from '@littlebird/sdk';
import { useState } from 'react';

type SectionState = {
  // Sections whose folded state the user flipped from the default; Snoozed starts folded
  toggled: readonly string[];
  // Sections the user opened past their first cards
  expanded: readonly string[];
};

const EMPTY: SectionState = { toggled: [], expanded: [] };

const storageKey = (viewKey: string) => `feed-sections:${viewKey}`;

// The tab keeps how the user left its sections for the rest of the session, across a reload or a visit elsewhere
const read = (viewKey: string): SectionState => {
  try {
    const raw = sessionStorage.getItem(storageKey(viewKey));
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'toggled' in parsed &&
      'expanded' in parsed &&
      Array.isArray(parsed.toggled) &&
      Array.isArray(parsed.expanded)
    )
      return {
        toggled: parsed.toggled.filter((id) => typeof id === 'string'),
        expanded: parsed.expanded.filter((id) => typeof id === 'string'),
      };
  } catch {
    // A blocked or corrupt store starts the sections at their defaults
  }
  return EMPTY;
};

const write = (viewKey: string, state: SectionState) => {
  try {
    sessionStorage.setItem(storageKey(viewKey), JSON.stringify(state));
  } catch {
    // The state still holds for this visit
  }
};

// Opens a tab's section and shows all its cards the next time the tab draws, as Home's "Show all" asks
export const showFeedSectionInFull = (viewKey: string, id: string) => {
  const state = read(viewKey);
  const folded = state.toggled.includes(id) !== (id === SNOOZED_FEED_GROUP);
  write(viewKey, {
    toggled: folded ? flip(state.toggled, id) : state.toggled,
    expanded: state.expanded.includes(id)
      ? state.expanded
      : [...state.expanded, id],
  });
};

const flip = (ids: readonly string[], id: string) =>
  ids.includes(id) ? ids.filter((each) => each !== id) : [...ids, id];

export const useFeedSections = (viewKey: string) => {
  const [state, setState] = useState(() => ({
    viewKey,
    ...read(viewKey),
  }));
  // A new tab reads its own sections
  const current =
    state.viewKey === viewKey ? state : { viewKey, ...read(viewKey) };
  if (current !== state) setState(current);

  const update = (next: SectionState) => {
    write(viewKey, next);
    setState({ viewKey, ...next });
  };

  return {
    isCollapsed: (id: string) =>
      current.toggled.includes(id) !== (id === SNOOZED_FEED_GROUP),
    isExpanded: (id: string) => current.expanded.includes(id),
    toggleCollapsed: (id: string) =>
      update({ ...current, toggled: flip(current.toggled, id) }),
    toggleExpanded: (id: string) =>
      update({ ...current, expanded: flip(current.expanded, id) }),
  };
};
